import type { Dispatch, FC, SetStateAction, SyntheticEvent } from 'react';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AbuseReasonValue } from 'features/call/vrs-call-comments/enums';
import type { VrsCallCommentsAbuseFormControl } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { FormControlLabel, Radio } from 'features/ui/elements';

interface VrsCallCommentsReasonRadioControlProps
  extends VrsCallCommentsAbuseFormControl {
  controlValue: AbuseReasonValue;
  selectedValue: string;
  setSelectedValueHandler: Dispatch<SetStateAction<string>>;
}

export const VrsCallCommentsReasonRadioControl: FC<
  VrsCallCommentsReasonRadioControlProps
> = ({ control, controlValue, selectedValue, setSelectedValueHandler }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'abuseCommentText',
  });

  const handleRadioChange = useCallback(
    (onChange: (event: SyntheticEvent, value: boolean) => void) =>
      (event: SyntheticEvent, value: boolean) => {
        setSelectedValueHandler((event.target as HTMLInputElement).value);
        return onChange(event, value);
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Controller
      control={control}
      name='commentReason'
      rules={{ required: true }}
      render={({ field: { onChange, ...args } }) => (
        <RadioControlLabel
          {...args}
          value={controlValue}
          label={t(controlValue)}
          control={<Radio />}
          checked={selectedValue === controlValue}
          onChange={handleRadioChange(onChange)}
        />
      )}
    />
  );
};

const RadioControlLabel = styled(FormControlLabel)`
  margin-top: ${theme.spacing(4)};
`;
